/**
 * Phase 132 · Global Tab-Scroll
 * ─────────────────────────────
 * When a user tabs through a long form and the newly-focused field falls
 * outside the viewport (or beneath the sticky Header), auto-scroll it into
 * view. Smooth, non-intrusive, and honours the platform's reduced-motion
 * preference.
 *
 * Fires only for KEYBOARD-driven focus (Tab / Shift+Tab / arrow) —
 * mouse-click focus never triggers a scroll so the operator stays in
 * control of pointer flows.
 */

const INTERACTIVE_SELECTOR =
  "input, select, textarea, button, [tabindex]:not([tabindex='-1']), a[href]";

const HEADER_HEIGHT = 76;   // matches sticky Header (px)
const BOTTOM_PAD    = 24;   // breathing room below the field

let lastKeyDownWasNavigation = false;

function isNavKey(e) {
  if (e.key === "Tab") return true;
  if ((e.key === "ArrowDown" || e.key === "ArrowUp") && (e.target?.matches?.("select") === false))
    return true;
  return false;
}

function shouldScroll(el) {
  if (!el || !(el instanceof Element)) return false;
  if (!el.matches?.(INTERACTIVE_SELECTOR)) return false;
  // Skip modals/dialogs that manage their own scroll internally.
  if (el.closest("[data-scroll-container]") || el.closest("[role='dialog']"))
    return false;
  return true;
}

function scrollIntoViewSafely(el) {
  const rect = el.getBoundingClientRect();
  const vpH  = window.innerHeight || document.documentElement.clientHeight;

  const hiddenAbove = rect.top    < HEADER_HEIGHT;
  const hiddenBelow = rect.bottom > vpH - BOTTOM_PAD;
  if (hiddenAbove || hiddenBelow) {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const behavior = reduced ? "auto" : "smooth";
    const farOff = Math.abs(rect.top) > vpH || rect.top > vpH * 1.5;
    el.scrollIntoView({ block: farOff ? "center" : "nearest", behavior });
  }
}

// Amber-pulse the newly-focused field so long-form fills feel guided.
// Removes itself after the animation completes so re-tabbing the same
// field always retriggers a fresh pulse.
function pulseFocus(el) {
  if (!el || !(el instanceof Element)) return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  if (reduced) return;
  el.classList.remove("cx-tab-pulse");
  // Force a reflow so re-adding the class always restarts the animation.
  // eslint-disable-next-line no-unused-expressions
  el.offsetWidth;
  el.classList.add("cx-tab-pulse");
  window.setTimeout(() => el.classList.remove("cx-tab-pulse"), 420);
}

export function installTabScroll() {
  const onKeyDown = (e) => { lastKeyDownWasNavigation = isNavKey(e); };
  const onFocusIn = (e) => {
    if (!lastKeyDownWasNavigation) return;
    lastKeyDownWasNavigation = false; // reset for the next event
    if (!shouldScroll(e.target)) return;
    // Delay a frame so any framework-driven layout shift settles first.
    requestAnimationFrame(() => {
      scrollIntoViewSafely(e.target);
      pulseFocus(e.target);
    });
  };

  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("focusin", onFocusIn, true);
  return () => {
    document.removeEventListener("keydown", onKeyDown, true);
    document.removeEventListener("focusin", onFocusIn, true);
  };
}
